/**
 * How far to scroll so a stuck category bar no longer covers the dish list.
 * Negative means scroll up. Zero when the bar is not sticky or the list
 * already starts at or below the bar.
 */
export function menuFlowRevealDelta(
  tabsBottom: number,
  contentTop: number,
  sticky: boolean,
): number {
  if (!sticky) {
    return 0;
  }
  const delta = contentTop - tabsBottom;
  return delta < -1 ? delta : 0;
}

/** True when the chip bar uses position:sticky (phone category chrome). */
function menuFlowTabsAreSticky(tabs: HTMLElement): boolean {
  return getComputedStyle(tabs).position === 'sticky';
}

function menuFlowScroller(node: HTMLElement): HTMLElement | Window {
  let current: HTMLElement | null = node.parentElement;
  while (current && current !== document.body && current !== document.documentElement) {
    const { overflowY } = getComputedStyle(current);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight + 1
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return window;
}

function scrollByY(scroller: HTMLElement | Window, delta: number): void {
  if (delta === 0) {
    return;
  }
  if (scroller instanceof HTMLElement) {
    scroller.scrollTop += delta;
    return;
  }
  /*
   * Prefer scrollingElement — window.scrollBy is unreliable in some iframe /
   * mobile WebView setups used by the studio device frame.
   */
  const root = document.scrollingElement;
  if (root) {
    root.scrollTop += delta;
    return;
  }
  scroller.scrollBy(0, delta);
}

/**
 * After a chip change, bring the new category's first dishes out from under the
 * sticky bar (phone) or snap the chip row back into view (wider layouts).
 * Measures `.ws-menu-flow__panels` (always in layout) — never a `display:none`
 * panel, whose getBoundingClientRect is all zeros.
 */
export function revealMenuFlowPanel(radio: HTMLInputElement): void {
  const chrome = radio.closest('.ws-menu-flow__chrome');
  if (!chrome) {
    return;
  }
  const tabs = chrome.querySelector('.ws-menu-flow__tabs');
  const panels = chrome.querySelector('.ws-menu-flow__panels');
  if (!(tabs instanceof HTMLElement) || !(panels instanceof HTMLElement)) {
    return;
  }

  const run = () => {
    const scroller = menuFlowScroller(panels);
    if (menuFlowTabsAreSticky(tabs)) {
      const delta = menuFlowRevealDelta(
        tabs.getBoundingClientRect().bottom,
        panels.getBoundingClientRect().top,
        true,
      );
      scrollByY(scroller, delta);
      return;
    }
    /*
     * Non-sticky (tablet+): chips scroll away with the page. Snap the chip row
     * back so the new category starts at the top of the scrollport.
     */
    const tabsTop = tabs.getBoundingClientRect().top;
    if (tabsTop < -1) {
      scrollByY(scroller, tabsTop);
    }
  };

  /*
   * Wait for :has() panel swap + height change, then correct again after
   * scroll anchoring may have fought the first adjustment.
   */
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
      window.setTimeout(run, 50);
    });
  });
}
